import { useRef } from 'react';
import { useLogImpression, useTracker } from '@archipro-website/tracker';
import type { ImageColumnProps } from '@modules/page-builder/types';
import type { ModuleProduct } from '@modules/page-builder/api/get-module-product.server';
import { trackTileMediaDetailClick } from '~/modules/tracking/util/trackTileMedia';
import { trackArticleViewProfileClick } from '~/modules/tracking/util/trackArticleProfessionalCta';

interface RevampProductMetaProps {
    column: ImageColumnProps;
    product: ModuleProduct | undefined;
    /** The product lookup has finished, whether or not it found one. */
    settled: boolean;
}

/**
 * Title, supplier and price under a featured product figure — the same
 * three lines a collection tile states, without replacing the figure.
 */
export const RevampProductMeta = ({
    column,
    product,
    settled,
}: RevampProductMetaProps) => {
    const tracker = useTracker();
    const metaRef = useRef<HTMLDivElement>(null);
    const productID = Number(product?.id);
    // The featured product counts as a listed product seen, as a directory
    // tile does; the hook skips it until the lookup has found one.
    useLogImpression({
        data:
            product && productID > 0
                ? {
                      type: 'LibraryItem_Directory',
                      itemID: productID,
                      professionalID: product.professionalId ?? 0,
                  }
                : undefined,
        ref: metaRef,
    });
    // The caption only stands in once the lookup has settled, so figure
    // prose never flashes as the product title and then reflows.
    const title = product?.productName || (settled ? column.caption || '' : '');
    const subtitle = product?.professionalName ?? '';
    const priceText = [product?.pricePrefix, product?.price]
        .filter(Boolean)
        .join(' ');

    if (!title && !subtitle && !priceText) return null;

    return (
        <div ref={metaRef} className="mt-4 flex flex-col md:mt-6">
            {title ? (
                product?.productLink ? (
                    <a
                        href={product.productLink}
                        onClick={() =>
                            trackTileMediaDetailClick(tracker, {
                                itemID: productID,
                                itemType: 'ProductTile',
                                itemName: title,
                            })
                        }
                        className="line-clamp-1 overflow-hidden text-sm font-medium tracking-[0.03rem] text-ellipsis text-[#323232] no-underline md:text-base md:tracking-[0.02rem]"
                    >
                        {title}
                    </a>
                ) : (
                    <p className="m-0 line-clamp-1 overflow-hidden text-sm font-medium tracking-[0.03rem] text-ellipsis text-[#323232] md:text-base md:tracking-[0.02rem]">
                        {title}
                    </p>
                )
            ) : null}
            {subtitle ? (
                product?.professionalLink ? (
                    <a
                        href={product.professionalLink}
                        onClick={() =>
                            trackArticleViewProfileClick(
                                tracker,
                                'imageParagraph',
                            )
                        }
                        className="line-clamp-1 overflow-hidden text-sm font-medium tracking-[0.03rem] text-ellipsis text-gray-900/40 no-underline md:text-base md:tracking-[0.02rem]"
                    >
                        {subtitle}
                    </a>
                ) : (
                    <p className="m-0 line-clamp-1 overflow-hidden text-sm font-medium tracking-[0.03rem] text-ellipsis text-gray-900/40 md:text-base md:tracking-[0.02rem]">
                        {subtitle}
                    </p>
                )
            ) : null}
            {priceText ? (
                <p className="m-0 mt-1 text-sm leading-none font-medium text-[#181818] md:text-base">
                    {priceText}
                </p>
            ) : null}
        </div>
    );
};
